import {
  DelegationChain,
  DelegationIdentity,
  Ed25519KeyIdentity,
  JsonnableDelegationChain,
  JsonnableEd25519KeyIdentity,
} from '@icp-sdk/core/identity'
import { Secp256k1KeyIdentity } from '@icp-sdk/core/identity/secp256k1'
import { Principal } from '@icp-sdk/core/principal'
import Wallet from '../wallet'
import { IKeyChain, KeychainType } from './IKeychain'

export interface SerializedDelegationKeychain {
  type: KeychainType.DelegationKeychain
  principalId: string
  sessionKey: JsonnableEd25519KeyIdentity
  delegationChain: JsonnableDelegationChain
  expiration: string
}

const privates = new WeakMap<
  IKeyChain,
  {
    sessionKey: Ed25519KeyIdentity
    delegationChain: DelegationChain
    principalId: string
    expiration: bigint
  }
>()

class DelegationKeychain implements IKeyChain {
  type: KeychainType.DelegationKeychain = KeychainType.DelegationKeychain

  async init(opts: SerializedDelegationKeychain) {
    return this.deserialize(opts)
  }

  async serialize(): Promise<SerializedDelegationKeychain> {
    const _privates = privates.get(this)!

    return {
      type: this.type,
      principalId: _privates.principalId,
      sessionKey: _privates.sessionKey.toJSON(),
      delegationChain: _privates.delegationChain.toJSON(),
      expiration: _privates.expiration.toString(),
    }
  }

  async deserialize(opts: SerializedDelegationKeychain) {
    const sessionKey = Ed25519KeyIdentity.fromJSON(JSON.stringify(opts.sessionKey))
    const delegationChain = DelegationChain.fromJSON(opts.delegationChain)

    privates.set(this, {
      sessionKey,
      delegationChain,
      principalId: opts.principalId,
      expiration: BigInt(opts.expiration),
    })
  }

  async addNewAccount(): Promise<Wallet[]> {
    throw new Error('DelegationKeychain does not support adding new accounts')
  }

  async getPrincipals(): Promise<Principal[]> {
    return [Principal.fromText(privates.get(this)!.principalId)]
  }

  async getIdentity(principal: string): Promise<DelegationIdentity> {
    const _privates = privates.get(this)!

    if (_privates.principalId !== principal) {
      throw new Error('Unable to find wallet')
    }

    const now = BigInt(Date.now()) * BigInt(1_000_000)
    if (now >= _privates.expiration) {
      throw new Error('Delegation has expired, please re-authenticate')
    }

    const delegationIdentity = DelegationIdentity.fromDelegation(
      _privates.sessionKey,
      _privates.delegationChain
    )

    // Attach session key as non-enumerable property for serialization
    // This allows the serializer to access the session key without exposing it
    Object.defineProperty(delegationIdentity, '__sessionKey__', {
      value: _privates.sessionKey,
      enumerable: false,
      writable: false,
      configurable: false,
    })

    return delegationIdentity
  }

  clear() {
    privates.delete(this)
  }

  async exportKeychain(): Promise<string> {
    throw new Error(
      'Cannot export Internet Identity delegation. Delegations are session-based and cannot be exported as mnemonic.'
    )
  }

  async exportPem(principal: string): Promise<string> {
    throw new Error(
      'PEM export not supported for DelegationKeychain. Internet Identity delegations cannot be exported in PEM format.'
    )
  }

  getSessionIdentity(): Promise<Secp256k1KeyIdentity | Ed25519KeyIdentity> {
    throw new Error('Method not implemented.')
  }
}

export default DelegationKeychain
